// Photographs on an asbestos record — upload, caption, delete. Each photo is stored
// directly on the record (separate from Document Library) via dedicated endpoints.
import { useRef, useState } from "react";
import { Camera, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { useOfflinePhotoQueue } from "@/hooks/useOfflinePhotoQueue";
import { PendingPhotoThumbs } from "@/components/PendingPhotoThumbs";

export function ZonePhotos({ zone, canEdit, buildingId, onLocalChange }) {
    const photos = zone.photos || [];
    const [viewing, setViewing] = useState(null);
    const fileRef = useRef(null);

    const { pendingPhotos, queuePhoto: queuePhotoOnly, retry: retryQueuedPhoto, discard: discardQueuedPhoto, discardAll: discardAllPendingPhotos } = useOfflinePhotoQueue({
        entityType: "asbestos_zone", entityId: zone.id, enabled: !!zone.id,
        recordLabel: `Asbestos — ${zone.label || zone.reference_id || "Untitled record"}`,
    });

    async function onPickFiles(e) {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        let queuedCount = 0;
        for (const file of files) {
            try { await queuePhotoOnly(file, { buildingId }); queuedCount += 1; }
            catch (err) { toast.error(`Could not save ${file.name} on this device`); }
        }
        if (!queuedCount) return;
        if (navigator.onLine) toast.success(queuedCount > 1 ? `${queuedCount} photos added — uploading…` : "Photo added — uploading…");
        else toast.info(queuedCount > 1 ? `${queuedCount} photos saved on this device — will upload automatically` : "Photo saved on this device — will upload automatically");
    }

    async function saveCaption(photoId, caption) {
        try {
            await api.patch(`/asbestos/zones/${zone.id}/photos/${photoId}`, new URLSearchParams({ caption: caption || "" }));
            onLocalChange({ photos: photos.map((p) => p.id === photoId ? { ...p, caption } : p) });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not save caption");
        }
    }

    async function deletePhoto(photoId) {
        try {
            await api.delete(`/asbestos/zones/${zone.id}/photos/${photoId}`);
            onLocalChange({ photos: photos.filter((p) => p.id !== photoId) });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not delete photo");
        }
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <div className="text-[10.5px] uppercase tracking-wider font-bold" style={{ color: "#9A968F" }}>Photographs</div>
                {canEdit && pendingPhotos.length > 0 && (
                    <button type="button" data-testid="asbestos-zone-discard-all-pending"
                        onClick={() => { if (window.confirm(`Discard all ${pendingPhotos.length} pending photo(s)? They will not be uploaded.`)) discardAllPendingPhotos(); }}
                        className="text-[10px] font-semibold hover:underline" style={{ color: "#9F4212" }}>
                        Discard all
                    </button>
                )}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
                {photos.map((p) => (
                    <div key={p.id} data-testid={`asbestos-zone-photo-${p.id}`} className="relative group rounded-[7px] overflow-hidden border" style={{ borderColor: "#E2DDD5" }}>
                        <img src={`${API}/asbestos/zones/${zone.id}/photos/${p.id}/file`} alt={p.caption || "Asbestos photo"}
                            className="w-full h-16 object-cover cursor-pointer" onClick={() => setViewing(p)} />
                        {canEdit && (
                            <button data-testid={`asbestos-zone-photo-delete-${p.id}`} onClick={() => deletePhoto(p.id)}
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full grid place-items-center bg-black/60 opacity-0 group-hover:opacity-100">
                                <Trash2 className="w-2.5 h-2.5 text-white" />
                            </button>
                        )}
                    </div>
                ))}
                {canEdit && (
                    <button data-testid="asbestos-zone-photo-add" onClick={() => fileRef.current?.click()}
                        className="h-16 rounded-[7px] border-2 border-dashed grid place-items-center" style={{ borderColor: "#E2DDD5" }}>
                        <Camera className="w-4 h-4" style={{ color: "#9A968F" }} />
                    </button>
                )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden disabled={!canEdit} onChange={onPickFiles} data-testid="asbestos-zone-photo-file" />

            <PendingPhotoThumbs pending={pendingPhotos} testId="asbestos-zone-pending-photos" onRetry={retryQueuedPhoto} onDiscard={canEdit ? discardQueuedPhoto : null} />

            {viewing && (
                <div className="fixed inset-0 z-[90] grid place-items-center p-4" style={{ background: "rgba(20,18,16,0.75)" }} onClick={() => setViewing(null)}>
                    <div className="max-w-lg w-full bg-white rounded-[12px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end p-1.5"><button onClick={() => setViewing(null)}><X className="w-4 h-4" style={{ color: "#6F6A64" }} /></button></div>
                        <img src={`${API}/asbestos/zones/${zone.id}/photos/${viewing.id}/file`} alt={viewing.caption || ""} className="w-full max-h-[60vh] object-contain" />
                        <div className="p-3">
                            <input data-testid="asbestos-zone-photo-caption" disabled={!canEdit} defaultValue={viewing.caption || ""}
                                onBlur={(e) => saveCaption(viewing.id, e.target.value.trim())}
                                placeholder="Add a caption / description…" className="w-full text-[12px] rounded-[7px] border px-2 py-1.5" style={{ borderColor: "#E2DDD5" }} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
